import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import {
  addEventCalendar,
  eventCalendarTypeList,
} from "../../../services/adminApiService/eventCalendarApis/eventCalendarApis.js";
import swal from "sweetalert";

// Validation of special character
const ALLOWED_CHARACTERS_REGEX = /^.*$/;

const initialFormData = {
  title: "",
  description: "",
  type_id: "",
  organized_by: "",
  start_date_time: "",
  end_date_time: "",
  location: "",
  remarks: "",
  no_ticket: "",
  no_price: "",
  video_link: "",
};

export const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// Helper function to get current date-time in YYYY-MM-DDTHH:mm format for datetime-local input
const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

function EventNotificationModal({
  showWebsiteModal,
  handleCloseWebsiteModal,
  fetchData,
  currentpage,
}) {
  const [formData, setFormData] = React.useState(initialFormData);
  const [typeData, setTypeData] = React.useState('');
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [fileUrl, setFileUrl] = React.useState(null);
  const [eventTypeList, seteventTypeList] = React.useState([]);
  const [importantDocFiles, setImportantDocFiles] = useState([null, null, null]);
  const [importantDocPreviews, setImportantDocPreviews] = useState([null, null, null]);
  const [Error, setError] = React.useState({
    titleError: "",
    descriptionError: "",
    locationError: "",
    remarksError: "",
    OrganinsationError: "",
    noTicketError: "",
    noPriceError: "",
    startDateError: "",
    endDateError: "",
  });
  const fileInputRef = React.useRef(null);

  // Function to validate start date
  const validateStartDate = (startDate) => {
    const now = new Date();
    const selectedStartDate = new Date(startDate);
    
    // Reset error first
    setError(prev => ({ ...prev, startDateError: "" }));
    
    if (!startDate) {
      return true; // Allow empty start date if not required
    }
    
    // Check if start date is in the past
    if (selectedStartDate < now) {
      setError(prev => ({ 
        ...prev, 
        startDateError: "Start date/time cannot be in the past" 
      }));
      return false;
    }
    
    return true;
  };

  // Function to validate end date
  const validateEndDate = (startDate, endDate) => {
    // Reset error first
    setError(prev => ({ ...prev, endDateError: "" }));
    
    if (!startDate || !endDate) {
      return true; // Allow empty dates if not required
    }
    
    const selectedStartDate = new Date(startDate);
    const selectedEndDate = new Date(endDate);
    
    // Check if end date is after start date
    if (selectedEndDate <= selectedStartDate) {
      setError(prev => ({ 
        ...prev, 
        endDateError: "End date/time must be after start date/time" 
      }));
      return false;
    }
    
    return true;
  };

  // Function to validate both dates together
  const validateDates = (startDate, endDate) => {
    const isStartValid = validateStartDate(startDate);
    const isEndValid = validateEndDate(startDate, endDate);
    return isStartValid && isEndValid;
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setSelectedFile(null);
    setFileUrl(null);
    setError({
      ...Error,
      startDateError: "",
      endDateError: ""
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate dates before submission
    if (!validateDates(formData.start_date_time, formData.end_date_time)) {
      swal("Date Validation Error!", "Please correct the date errors before submitting.", "error");
      return;
    }

    if (Object.values(Error).some(Boolean)) {
      swal("Validation failed!", "", "error");
      return;
    }



    const newFormData = new FormData();
    const updatedFormData = {
      ...formData,
      start_date_time: formData.start_date_time && formatDate(formData.start_date_time),
      end_date_time: formData.end_date_time && formatDate(formData.end_date_time),
    };
    
    // Append each key-value pair from updatedFormData to newFormData
    Object.keys(updatedFormData).forEach((key) => {
      if (updatedFormData[key] !== undefined && updatedFormData[key] !== null) {
        newFormData.append(`${key}`, updatedFormData[key]);
      }

    });

    if (selectedFile) {
      newFormData.append("attachement", selectedFile);
    }
    
    [0, 1, 2].forEach((idx) => {
      const file = importantDocFiles[idx];
      if (file) {
        newFormData.append(`important_documents[${idx}]`, file);
      }
    });
    
    try {
      let res = await addEventCalendar(newFormData);
      if (res.data.status && res.data.status !== "error") {
        handleCloseWebsiteModal();
        swal("Event Created!", "", "success");
        setTypeData('');
        fetchData({ currentpage });
        handleReset();
      } else {
        swal(`${res.data.status}`, "", "error");
      }
    } catch (error) {
      swal(`${error}`, "", "error");
    }
  };

  // Handle input changes
  const handleChangeInputValue = (e) => {
    let { name, value } = e.target;
    value = value.trimStart();
    
    if (name === "title") {
      if (!ALLOWED_CHARACTERS_REGEX.test(value)) {
        setError({ ...Error, titleError: "You can't use special Character" });
        return;
      } else {
        setError({ ...Error, titleError: "" });
      }
    }

    if (name === "start_date_time") {
      // Validate start date
      if (!validateStartDate(value)) {
        // Don't update form data if validation fails
        return;
      }
      
      // Validate end date if it exists
      if (formData.end_date_time) {
        validateEndDate(value, formData.end_date_time);
      }
    }

    if (name === "end_date_time") {
      if (formData.start_date_time) {
        if (!validateEndDate(formData.start_date_time, value)) {
          // Don't update form data if validation fails
          return;
        }
      }
      
      // Also validate that end date is not in the past if there's no start date
      if (!formData.start_date_time) {
        const now = new Date();
        const selectedEndDate = new Date(value);
        if (selectedEndDate < now) {
          setError(prev => ({ 
            ...prev, 
            endDateError: "End date/time cannot be in the past" 
          }));
          return;
        }
      }
    }

    if (name === "no_ticket") {
      if (!/^\d*$/.test(value)) {
        setError({ ...Error, noTicketError: "Only numeric values are allowed." });
        return;
      } else {
        setError({ ...Error, noTicketError: "" });
      }
    }
    
    if (name === "type_id") {
      const selected = eventTypeList.find((item) => item.id.toString() === value);
      setTypeData(selected?.name);
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const allowedExtensions = /(\.jpg|\.jpeg|\.webp|\.png|\.pdf)$/i;
      const maxFileSize = 10 * 1024 * 1024; // 10 MB

      if (!allowedExtensions.test(file.name)) {
        swal(
          "Error",
          "Invalid file type. Only jpg, jpeg, webp, png, and pdf files are allowed.",
          "error"
        );
        setSelectedFile(null);
        setFileUrl(null);
        return;
      }

      if (file.size > maxFileSize) {
        swal("Error", "File size should be less than 10 MB.", "error");
        setSelectedFile(null);
        setFileUrl(null);
        return;
      }

      // Set valid file
      setSelectedFile(file);
      setFileUrl(URL.createObjectURL(file));
      swal("", "File is selected", "success");
    } else {
      setSelectedFile(null);
      setFileUrl(null);
    }
  };
  
  const handleImportantDocFileChange = (idx, e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const allowedExtensions = /(\.jpg|\.jpeg|\.webp|\.png|\.pdf)$/i;
      const maxFileSize = 10 * 1024 * 1024; // 10 MB

      if (!allowedExtensions.test(file.name)) {
        swal(
          "Error",
          "Invalid file type. Only jpg, jpeg, webp, png, and pdf files are allowed.",
          "error"
        );
        const updatedFiles = [...importantDocFiles];
        updatedFiles[idx] = null;
        setImportantDocFiles(updatedFiles);
        return;
      }

      if (file.size > maxFileSize) {
        swal("Error", "File size should be less than 10 MB.", "error");
        const updatedFiles = [...importantDocFiles];
        updatedFiles[idx] = null;
        setImportantDocFiles(updatedFiles);
        return;
      }

      // Set valid file
      const updatedFiles = [...importantDocFiles];
      updatedFiles[idx] = file;
      setImportantDocFiles(updatedFiles);

      // Create preview URL
      const updatedPreviews = [...importantDocPreviews];
      updatedPreviews[idx] = URL.createObjectURL(file);
      setImportantDocPreviews(updatedPreviews);

      swal("", "File is selected", "success");
    } else {
      const updatedFiles = [...importantDocFiles];
      updatedFiles[idx] = null;
      setImportantDocFiles(updatedFiles);

      const updatedPreviews = [...importantDocPreviews];
      updatedPreviews[idx] = null;
      setImportantDocPreviews(updatedPreviews);
    }
  };

  const handleImportantDocPreview = (idx) => {
    if (importantDocPreviews[idx]) {
      window.open(importantDocPreviews[idx], "_blank");
    }
  };

  const handleFilePreview = () => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const eventListHandler = async () => {
    try {
      const response = await eventCalendarTypeList();
      seteventTypeList(response?.data?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    eventListHandler();
  }, []);

  return (
    <Modal show={showWebsiteModal} onHide={handleCloseWebsiteModal}>
      <form onSubmit={handleSubmit}>
        <div className="modal-header">
          <h4 className="modal-title fs-20">Create Event</h4>
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseWebsiteModal}
          >
            <span></span>
          </button>
        </div>
        <div className="modal-body">
          <div className="add-contact-box">
            <div className="add-contact-content">
              {/* 1st Row */}
              <div className="row">
                <div className="form-group mb-3 col-md-12">
                  <label className="text-black font-w500">
                    Title <span className="text-danger">*</span>
                  </label>
                  <div className="contact-name">
                    <Form.Control
                      required
                      name="title"
                      value={formData.title}
                      placeholder="Enter Title"
                      onChange={handleChangeInputValue}
                    />
                  </div>
                  <span className="validation-text text-danger">
                    {Error.titleError && Error.titleError}
                  </span>
                </div>
              </div>
              
              {/* 2nd Row */}
              <div className="row">
                <div className="form-group mb-3 col-md-12">
                  <label className="text-black font-w500">
                    Description <span className="text-danger">*</span>
                  </label>
                  <div className="contact-name">
                    <Form.Control
                      required
                      name="description"
                      as="textarea"
                      placeholder="Description"
                      onChange={handleChangeInputValue}
                      value={formData.description}
                    />
                  </div>
                  <span className="validation-text text-danger">
                    {Error.descriptionError && Error.descriptionError}
                  </span>
                </div>
              </div>
              
              {/* 3rd row */}
              <div className="row">
                <div className="form-group mb-3 col-md-6">
                  <label className="text-black font-w500">Type <span className="text-danger">*</span></label>
                  <select
                    required
                    name="type_id"
                    className="form-control"
                    value={formData.type_id}
                    onChange={handleChangeInputValue}
                  >
                    <option value={""}>Select type</option>
                    {eventTypeList.map((data) => (
                      <option key={data.id} value={data.id}>
                        {data.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group mb-3 col-md-6">
                  <label className="text-black font-w500">Organized By</label>
                  <div className="contact-name">
                    <Form.Control
                      name="organized_by"
                      value={formData.organized_by}
                      placeholder="Enter Organization name"
                      onChange={handleChangeInputValue}
                    />
                  </div>
                </div>
              </div>
              
              {/* 4th row Start Date & End Date */}
              <div className="row">
                <div className="form-group mb-3 col-md-6">
                  <label className="text-black font-w500">Start Date <span className="text-danger">*</span></label>
                  <Form.Group controlId="startDate">
                    <Form.Control
                      type="datetime-local"
                      name="start_date_time"
                      value={formData.start_date_time}
                      onChange={handleChangeInputValue}
                      min={getCurrentDateTimeLocal()} // Prevents selecting past dates in UI
                      required
                    />
                    {Error.startDateError && (
                      <div className="validation-text text-danger small mt-1">
                        {Error.startDateError}
                      </div>
                    )}
                  </Form.Group>
                </div>

                <div className="form-group mb-3 col-md-6">
                  <label className="text-black font-w500">End Date <span className="text-danger">*</span></label>
                  <Form.Group controlId="endDate">
                    <Form.Control
                      type="datetime-local"
                      name="end_date_time"
                      value={formData.end_date_time}
                      onChange={handleChangeInputValue}
                      min={formData.start_date_time || getCurrentDateTimeLocal()} // Can't be before start date
                      required
                    />
                    {Error.endDateError && (
                      <div className="validation-text text-danger small mt-1">
                        {Error.endDateError}
                      </div>
                    )}
                  </Form.Group>
                </div>
              </div>

              {typeData === "Sports Event" && (
                <div className="row">
                  <div className="form-group mb-3 col-md-6">
                    <label className="text-black font-w500">No Of Tickets</label>
                    <div className="contact-name">
                      <Form.Control
                        type="number"
                        name="no_ticket"
                        value={formData.no_ticket}
                        placeholder="No Of Tickets"
                        onChange={handleChangeInputValue}
                        min="0"
                      />
                      {Error.noTicketError && (
                        <small className="text-danger">{Error.noTicketError}</small>
                      )}
                    </div>
                  </div>

                  <div className="form-group mb-3 col-md-6">
                    <label className="text-black font-w500">Per Ticket Price</label>
                    <div className="contact-name">
                      <Form.Control
                        type="number"
                        name="no_price"
                        value={formData.no_price}
                        placeholder="Per Ticket Price"
                        onChange={handleChangeInputValue}
                        min="0"
                      />
                      {Error.noPriceError && (
                        <small className="text-danger">{Error.noPriceError}</small>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 5th row */}
              <div className="row">
                <div className="form-group mb-3 col-md-6">
                  <label className="text-black font-w500">Location</label>
                  <div className="contact-name">
                    <Form.Control
                      name="location"
                      placeholder="Location"
                      value={formData.location}
                      onChange={handleChangeInputValue}
                    />
                  </div>
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="text-black font-w500">Remarks</label>
                  <div className="contact-name">
                    <Form.Control
                      name="remarks"
                      placeholder="Remarks"
                      value={formData.remarks}
                      onChange={handleChangeInputValue}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="form-group mb-3 col-md-12">
                  <Form.Group controlId="formFile">
                    <Form.Label className="text-black font-w500">
                      Upload Document
                    </Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleFileChange}
                      />
                      <Button
                        variant="primary"
                        className="ms-2"
                        disabled={!selectedFile}
                        onClick={handleFilePreview}
                      >
                        Preview
                      </Button>
                    </div>
                  </Form.Group>
                </div>
              </div>
              
              {/* Upload Video Link Row */}
              <div className="row">
                <div className="form-group mb-3 col-md-12">
                  <Form.Group controlId="formVideoLink">
                    <Form.Label className="text-black font-w500">
                      Upload Video Link
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="video_link"
                      placeholder="Paste video link here"
                      value={formData.video_link || ''}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </div>
              </div>
              
              {/* Upload Important Document Section */}
              <div className="row">
                <div className="form-group mb-3 col-md-12">
                  <Form.Label className="text-black font-w500">
                    Upload Important Document
                  </Form.Label>
                  {[0, 1, 2].map((idx) => (
                    <div key={idx} className="row mb-3 p-2 border rounded align-items-center gx-2 gy-1">
                      <div className="col-12 col-md-4">
                        <Form.Control
                          type="text"
                          name={`important_document_title_${idx}`}
                          placeholder={`Title`}
                          value={formData[`important_document_title_${idx}`] || ''}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-12 col-md-5">
                        <Form.Control
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => handleImportantDocFileChange(idx, e)}
                        />
                      </div>
                      <div className="col-12 col-md-3 d-flex justify-content-md-start justify-content-center mt-2 mt-md-0">
                        <Button
                          variant="primary"
                          className="ms-md-2 btn-sm"
                          disabled={!importantDocFiles[idx]}
                          onClick={() => handleImportantDocPreview(idx)}
                        >
                          Preview
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer d-flex justify-content-between">
          <div className="d-flex justify-content-end w-100">
            <button
              type="button"
              className="btn btn-secondary mx-2"
              onClick={handleReset}
            >
              Reset
            </button>
            <Button type="submit" className="btn btn-primary mx-2">
              Raise
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default React.memo(EventNotificationModal);